"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import logo from "@/public/global/logo.svg";
import Image from "next/image";
import { stegaClean } from "@sanity/client/stega";
import { GROQButton, GROQCTA } from "@/sanity/lib/definitions";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";

// An editable CTA (Sanity `button`) counts as "set" when it references a CTA
// with a title. We only pull the label here and let <CTAWrapper> resolve the
// destination/behavior (link, anchor, or popup) just like the page CTAs do.
function navCtaLabel(cta: GROQButton | undefined): string | null {
  const clean = (stegaClean(cta?.cta ?? null) ?? null) as GROQCTA | null;
  return clean?.title || null;
}

const NAV_ITEMS = [
  { label: "Why WCE", target: "why-wce" },
  { label: "Trailer", target: "trailer" },
  { label: "Our Mentors", target: "mentors" },
  { label: "What to expect", target: "manifesto" },
  { label: "WCE vs Others", target: "comparison" },
  { label: "FAQ", target: "faq" },
];

function HamburgerIcon({ isLight }: { isLight: boolean }) {
  const bar = cn("w-full rounded-full h-[0.2rem] transition-colors duration-300", isLight ? "bg-black" : "bg-white");
  return (
    <span className="flex flex-col w-[3.4rem] gap-[0.6rem]">
      <span className={bar} />
      <span className={bar} />
      <span className={bar} />
    </span>
  );
}

function CloseIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-[2.4rem] w-[2.4rem]">
      <path
        d="M5 5L19 19M19 5L5 19"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function HomeNavbar({ loginCTA, signupCTA, className }: { loginCTA?: GROQButton; signupCTA?: GROQButton, className?:string }) {
  const [activeId, setActiveId] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const navRef = useRef<HTMLElement>(null);

  const isLight = theme === "light";

  const loginLabel = navCtaLabel(loginCTA);
  const signupLabel = navCtaLabel(signupCTA);

  // Flip the navbar colors based on the background tone of the section under it.
  // Sections with a light background opt in via `data-navbar-theme="light"`.
  useEffect(() => {
    const marked = Array.from(document.querySelectorAll<HTMLElement>("[data-navbar-theme]"));
    if (marked.length === 0) return;

    const update = () => {
      const nav = navRef.current;
      if (!nav) return;
      const rect = nav.getBoundingClientRect();
      const line = rect.top + rect.height / 2; // navbar vertical center

      let next: "dark" | "light" = "dark";
      for (const el of marked) {
        const box = el.getBoundingClientRect();
        if (line >= box.top && line <= box.bottom) {
          next = el.dataset.navbarTheme === "light" ? "light" : "dark";
          break;
        }
      }
      setTheme(next);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // Highlight the nav item whose section is currently at the reference line.
  // Sections vary a lot in height (the trailer's video frame is tall), so an
  // intersection-ratio comparison is unreliable — a tall section fills the
  // detection band but scores a low ratio and loses to shorter neighbours.
  // Instead, pick the last section (in document order) whose top has crossed a
  // line near the top of the viewport. Exactly one section wins, regardless of
  // height, and nothing is active until the first section reaches the line.
  useEffect(() => {
    const sections = NAV_ITEMS.map((item) => document.getElementById(item.target)).filter(Boolean) as HTMLElement[];

    if (sections.length === 0) return;

    const update = () => {
      const line = window.innerHeight * 0.3;
      let current = "";
      for (const el of sections) {
        if (el.getBoundingClientRect().top <= line) current = el.id;
        else break;
      }
      setActiveId(current);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <>
      <div className={cn("sticky z-50 w-[min(150rem,100%)] mx-auto top-[1.5rem] px-[1.6rem] lg:px-0 ",className)}>
        <nav
          ref={navRef}
          className={cn(
            "flex justify-between items-center border gap-[2rem] rounded-[2rem] py-[1rem] pl-[1.75rem] pr-[1rem] backdrop-blur-[1rem] h-[7rem] lg:pr-[1rem] transition-colors duration-300",
            isLight ? "border-black/[0.08] bg-black/[0.04]" : "border-white/[0.08] bg-white/[0.06]",
          )}>
          {/* Logo */}
          <a
            href="#top"
            className="flex items-center shrink-0 gap-[1rem]">
            <Image
              src={logo}
              className={cn("w-[20rem] transition-[filter] duration-300", isLight && "invert")}
              alt=""
            />
          </a>

          {/* Nav links (desktop) */}
          <div className="hidden absolute top-1/2 left-1/2 items-center whitespace-nowrap -translate-x-1/2 -translate-y-1/2 lg:flex gap-[2.2rem]">
            {NAV_ITEMS.map((item) => {
              const isActive = activeId === item.target;
              return (
                <a
                  key={item.target}
                  href={`#${item.target}`}
                  className={cn(
                    "font-geist text-[1.75rem] leading-[2.4rem] transition-[opacity,color] duration-200",
                    isLight ? "text-black" : "text-white",
                    isActive
                      ? "font-semibold underline decoration-[#7bcdff] decoration-[0.1rem] underline-offset-[0.4rem] opacity-80"
                      : "font-normal opacity-60 hover:opacity-100",
                  )}>
                  {item.label}
                </a>
              );
            })}
          </div>

          {/* CTAs (desktop) */}
          <div className="hidden items-center lg:flex shrink-0 gap-[0.8rem]">
            {loginLabel && (
              <CTAWrapper
                cta={loginCTA?.cta}
                className={cn(
                  "flex items-center font-medium capitalize transition-colors duration-200 rounded-[1rem] px-[2.2rem] py-[0.9rem] font-geist text-[1.75rem] tracking-[-0.07rem]",
                  isLight ? "text-black hover:bg-black/5" : "text-white hover:bg-white/5",
                )}>
                {loginLabel}
              </CTAWrapper>
            )}
            {signupLabel && (
              <CTAWrapper
                cta={signupCTA?.cta}
                className={cn(
                  "flex items-center font-medium capitalize transition-colors duration-200 px-[2.2rem] py-[0.9rem] font-geist text-[1.75rem] tracking-[-0.07rem] rounded-[1.25rem] cursor-pointer",
                  isLight ? "bg-black text-white hover:bg-black/90" : "bg-white text-black hover:bg-white/90",
                )}>
                {signupLabel}
              </CTAWrapper>
            )}
          </div>

          {/* Hamburger (mobile) */}
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setIsOpen(true)}
            className="flex justify-center items-center lg:hidden shrink-0 pr-[0.75rem]">
            <HamburgerIcon isLight={isLight} />
          </button>
        </nav>
      </div>

      {/* Mobile menu overlay */}
      <div
        className={cn(
          "fixed inset-0 z-[60] flex flex-col bg-black p-[2rem] pb-[3rem] transition-transform duration-300 ease-out lg:hidden px-[3.75rem]",
          isOpen ? "translate-y-0" : "-translate-y-full pointer-events-none",
        )}>
        {/* Top bar: logo + close */}
        <div className="flex justify-between items-center h-[5rem]">
          <a
            href="#top"
            onClick={() => setIsOpen(false)}
            className="flex items-center shrink-0">
            <Image
              src={logo}
              className="w-[20rem]"
              alt=""
            />
          </a>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setIsOpen(false)}
            className="flex justify-center items-center p-[1rem] -mr-[1rem]">
            <CloseIcon />
          </button>
        </div>

        {/* Nav list */}
        <div className="flex flex-col mt-[3rem]">
          {NAV_ITEMS.map((item, i) => {
            const isActive = activeId === item.target;
            return (
              <a
                key={item.target}
                href={`#${item.target}`}
                onClick={() => setIsOpen(false)}
                className={cn(
                  "py-[2rem] font-geist text-[2.5rem] leading-[2.4rem] text-white transition-opacity duration-200",
                  i < NAV_ITEMS.length - 1 && "border-b border-white/10",
                  isActive
                    ? "font-semibold underline decoration-[#7bcdff] decoration-[0.1rem] underline-offset-[0.4rem] opacity-80"
                    : "font-normal opacity-60",
                )}>
                {item.label}
              </a>
            );
          })}
        </div>

        {/* Join CTA */}
        {signupLabel && (
          <div
            onClick={() => setIsOpen(false)}
            className="mt-auto w-full cursor-pointer">
            <CTAWrapper
              cta={signupCTA?.cta}
              className="flex justify-center items-center w-full font-medium text-black capitalize transition-colors duration-200 cursor-pointer rounded-[1rem] bg-[#98d8ff] px-[4rem] py-[1.4rem] font-geist text-[1.6rem] tracking-[-0.08rem] hover:bg-[#85cdfa]">
              {signupLabel}
            </CTAWrapper>
          </div>
        )}
      </div>
    </>
  );
}
